import { Outlet, Link, useNavigate } from "react-router-dom";
import { HardHat, LayoutDashboard, FolderOpen, ClipboardList, FileText, Package, Users, Truck, TrendingUp, LogOut, Menu, X, UserCheck, Shield, Briefcase, Building2, Wrench, DollarSign, FileBarChart, ShoppingCart, Camera, BookOpen, ClipboardCheck, Key, CalendarDays, Database, Warehouse, Bell, Check, Trash2, Eye, EyeOff, Archive, Clock } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { apiClient } from "../utils/apiClient";
import { clearAuth, setAuth, logout } from "../utils/authService";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Auto-logout timeout: 15 minutes (900000 ms)
const AUTO_LOGOUT_TIMEOUT = 15 * 60 * 1000;
const WARNING_BEFORE_LOGOUT = 60 * 1000; // 1 minute warning

const DashboardLayout = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ oldPassword: "", newPassword: "", confirmPassword: "" });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const notificationRef = useRef(null);
  
  // Auto-logout state
  const logoutTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const warningShownRef = useRef(false);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(AUTO_LOGOUT_TIMEOUT);

  // Auto-logout function
  const handleAutoLogout = useCallback(() => {
    logout();
    setUser(null);
    toast.error('Session expired due to inactivity. Please login again.', { duration: 5000 });
    navigate('/login');
  }, [navigate, setUser]);

  // Show warning before logout
  const showLogoutWarning = useCallback(() => {
    if (!warningShownRef.current) {
      warningShownRef.current = true;
      toast.warning(
        <div className="flex items-center space-x-2">
          <Clock className="w-4 h-4" />
          <span>Session expires in 1 minute. Move your mouse to stay logged in.</span>
        </div>,
        { duration: 55000, id: 'session-warning' }
      );
    }
  }, []);

  // Reset auto-logout timer on user activity
  const resetAutoLogoutTimer = useCallback(() => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    
    warningShownRef.current = false;
    toast.dismiss('session-warning');
    
    setSessionTimeLeft(AUTO_LOGOUT_TIMEOUT);
    
    // Set warning timer (1 minute before logout)
    warningTimerRef.current = setTimeout(showLogoutWarning, AUTO_LOGOUT_TIMEOUT - WARNING_BEFORE_LOGOUT);
    
    // Set logout timer
    logoutTimerRef.current = setTimeout(handleAutoLogout, AUTO_LOGOUT_TIMEOUT);
  }, [handleAutoLogout, showLogoutWarning]);

  // Setup auto-logout listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    let lastActivity = Date.now();
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastActivity > 2000) { // Throttle: only reset if 2+ seconds since last activity
        lastActivity = now;
        resetAutoLogoutTimer();
      }
    };

    events.forEach(event => {
      document.addEventListener(event, throttledReset, { passive: true });
    });

    // Start initial timer
    resetAutoLogoutTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledReset);
      });
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    };
  }, [resetAutoLogoutTimer]);

  useEffect(() => {
    fetchPermissions();
    fetchNotifications();
    fetchUnreadCount();
    if (user?.role === "Master Admin" || user?.role === "Management") {
      fetchPendingUsers();
    }
    
    // SEC-001 fix: Force password change dialog if required
    if (user?.must_change_password) {
      setChangePasswordOpen(true);
      toast.warning("You must change your password before continuing", { duration: 5000 });
    }
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [user]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await apiClient.get('/permissions');
      setPermissions(response.data.permissions);
    } catch (error) {
      console.error("Failed to fetch permissions");
    }
  };
  
  const fetchNotifications = async () => {
    try {
      const response = await apiClient.get('/notifications?limit=20');
      setNotifications(response.data);
    } catch (error) {
      console.error("Failed to fetch notifications");
    }
  };
  
  const fetchUnreadCount = async () => {
    try {
      const response = await apiClient.get('/notifications/unread-count');
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error("Failed to fetch unread count");
    }
  };
  
  const markAsRead = async (notificationId) => {
    try {
      await apiClient.put(`/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read");
    }
  };
  
  const markAllAsRead = async () => {
    try {
      await apiClient.put('/notifications/mark-all-read');
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch (error) {
      toast.error("Failed to mark all as read");
    }
  };
  
  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
      setNotificationDropdownOpen(false);
    }
  };
  
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const fetchPendingUsers = async () => {
    try {
      const response = await apiClient.get('/users/pending');
      setPendingCount(response.data.length);
    } catch (error) {
      console.error("Failed to fetch pending users");
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      // SEC-004 fix: Send password in request body, not URL params
      await apiClient.post('/auth/change-password', {
        old_password: passwordForm.oldPassword,
        new_password: passwordForm.newPassword
      });
      toast.success("Password changed successfully");
      setChangePasswordOpen(false);
      setPasswordForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
      
      // Update user state to clear must_change_password flag
      if (user?.must_change_password) {
        const updatedUser = { ...user, must_change_password: false };
        setAuth(null, updatedUser); // Update user in storage without changing token
        setUser(updatedUser);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const isMasterAdmin = user?.email === "shiva.mittal@kpminfra.com";
  const isHRUser = user?.role === "HR Manager";

  const hasPermission = (permission) => {
    return permissions.includes("all") || permissions.includes(permission);
  };

  const handleLogout = () => {
    clearAuth();
    setUser(null);
    navigate("/login");
  };

  const menuItems = [
    // Dashboard not shown for HR Manager - they go directly to Projects
    ...(!isHRUser ? [{ icon: LayoutDashboard, label: "Dashboard", path: "/dashboard", permission: null }] : []),
    { icon: FolderOpen, label: "Projects", path: "/projects", permission: "projects" },
    { icon: Warehouse, label: "Master Equipment", path: "/master-equipment", permission: "equipment" },
    { icon: Briefcase, label: "HR Management", path: "/hr", permission: "hr" },
    { icon: Building2, label: "Contractors", path: "/contractors", permission: "contractors" },
    { icon: Wrench, label: "Maintenance", path: "/maintenance", permission: "equipment" },
    { icon: ShoppingCart, label: "Purchase Orders", path: "/purchase-orders", permission: "materials" },
    { icon: DollarSign, label: "Billing", path: "/billing", permission: "billing" },
    { icon: FileText, label: "Payroll", path: "/payroll", permission: "hr" },
    { icon: CalendarDays, label: "Attendance Report", path: "/attendance-report", permission: "workforce" },
    { icon: Camera, label: "Site Photos", path: "/site-photos", permission: "dpr" },
    { icon: BookOpen, label: "Site Diary", path: "/site-diary", permission: "dpr" },
    { icon: Shield, label: "Safety", path: "/safety", permission: "dpr" },
    { icon: ClipboardCheck, label: "Quality Control", path: "/quality", permission: "dpr" },
    { icon: FileBarChart, label: "Reports", path: "/reports", permission: "analytics" },
    { icon: Database, label: "Data Management", path: "/data-management", permission: "all" },
    // Archive Management only visible to Master Admin and Project Managers
    ...((isMasterAdmin || user?.role === "Project Manager") ? [{ icon: Archive, label: "Archive Management", path: "/archives", permission: "all" }] : []),
    // User Approvals only visible to Master Admin
    ...(isMasterAdmin ? [{ icon: UserCheck, label: "User Approvals", path: "/user-approvals", permission: "all", badge: pendingCount }] : []),
  ].filter(item => !item.permission || hasPermission(item.permission));

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''} flex flex-col h-screen safe-area-top`}>
        {/* Header - Fixed height */}
        <div className="p-4 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <img 
              src="/kpm-logo.png" 
              alt="KPM Infrastructure" 
              className="w-12 h-12 object-contain bg-white rounded-sm p-1"
            />
            <div>
              <h1 className="font-heading font-bold text-sm tracking-tight leading-tight">KPM INFRASTRUCTURE</h1>
              <p className="text-xs text-slate-400 uppercase tracking-widest">Pvt. Ltd.</p>
            </div>
          </div>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto min-h-0">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              className="flex items-center justify-between px-4 py-2.5 rounded-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
              onClick={() => setSidebarOpen(false)}
            >
              <div className="flex items-center space-x-3">
                <item.icon className="w-4 h-4" />
                <span className="font-medium text-sm">{item.label}</span>
              </div>
              {item.badge > 0 && (
                <span className="bg-accent text-white text-xs font-bold px-2 py-1 rounded-full">
                  {item.badge}
                </span>
              )}
            </Link>
          ))}
        </nav>

        {/* User Profile - Fixed at bottom */}
        <div className="p-4 border-t border-slate-700 flex-shrink-0">
          <div className="mb-3 px-4">
            <p className="text-xs text-slate-400 uppercase tracking-widest mb-1">User</p>
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400">{user?.role}</p>
          </div>
          <button
            onClick={() => setChangePasswordOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 rounded-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors w-full mb-1"
          >
            <Key className="w-4 h-4" />
            <span className="text-sm font-medium">Change Password</span>
          </button>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex items-center space-x-2 px-4 py-2 rounded-sm text-slate-300 hover:bg-red-900/20 hover:text-red-400 transition-colors w-full"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Current Password *</label>
              <div className="relative">
                <input
                  type={showOldPassword ? "text" : "password"}
                  required
                  value={passwordForm.oldPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, oldPassword: e.target.value})}
                  className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showOldPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">New Password *</label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  required
                  minLength={6}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                  className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Confirm New Password *</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={() => setChangePasswordOpen(false)} className="btn-outline">Cancel</button>
              <button type="submit" disabled={changingPassword} className="btn-primary">
                {changingPassword ? "Changing..." : "Change Password"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-toggle"
        className="fixed top-4 left-4 z-50 md:hidden bg-primary text-white p-2 rounded-sm"
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      <main className="main-content flex-1 min-w-0">
        <div className="bg-white border-b border-slate-200 pl-20 pr-6 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading font-bold text-lg text-primary">KPM Infrastructure Private Limited</h2>
              <p className="text-xs text-slate-600 uppercase tracking-widest">Project Management System</p>
            </div>
            <div className="flex items-center space-x-4">
              {/* Notification Bell */}
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => {
                    setNotificationDropdownOpen(!notificationDropdownOpen);
                    if (!notificationDropdownOpen) fetchNotifications();
                  }}
                  className="relative p-2 text-slate-600 hover:text-primary hover:bg-slate-100 rounded-full transition-colors"
                  data-testid="notification-bell"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-error text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>
                
                {/* Notification Dropdown */}
                {notificationDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-slate-200 z-50 max-h-[500px] overflow-hidden">
                    <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                      <h3 className="font-heading font-bold text-sm">Notifications</h3>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllAsRead}
                          className="text-xs text-primary hover:underline flex items-center space-x-1"
                        >
                          <Check className="w-3 h-3" />
                          <span>Mark all read</span>
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center text-slate-500">
                          <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                          <p className="text-sm">No notifications</p>
                        </div>
                      ) : (
                        notifications.map((notification) => (
                          <div
                            key={notification.id}
                            onClick={() => handleNotificationClick(notification)}
                            className={`p-4 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                              !notification.is_read ? 'bg-primary/5' : ''
                            }`}
                          >
                            <div className="flex items-start space-x-3">
                              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                                !notification.is_read ? 'bg-primary' : 'bg-transparent'
                              }`} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm ${!notification.is_read ? 'font-semibold' : 'font-medium'} text-slate-800`}>
                                  {notification.title}
                                </p>
                                <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                  {formatTimeAgo(notification.created_at)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {notifications.length > 0 && (
                      <div className="p-3 border-t border-slate-200 text-center">
                        <button
                          onClick={() => {
                            navigate('/notifications');
                            setNotificationDropdownOpen(false);
                          }}
                          className="text-xs text-primary hover:underline"
                        >
                          View all notifications
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="text-right">
                <p className="text-sm font-medium text-slate-700">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;